import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { AssignExamAdminAction, ClearExamineesAction, ExamineeAction, GetExamApplicantsAction, GetExamApplicantsShortAction, GetSingleExamApplicantAction, ReassignExamineeAction, UpdateExamineeStatusAction } from './examinee.actions';
import { LoadingState } from '@tsin-core/entities';
import { ApplicationService } from '@tsin-core/services/http/application.service';
import { catchError, tap } from 'rxjs';
import { IExaminee, IExamineeShort } from '@tsin-core/models/application.model';

export class ExamineeStateModel {
  public examineeList!: IExaminee[];
  public examineeShortList!: IExamineeShort[];
  public loading!: LoadingState;
}

const defaults = {
  examineeList: [],
  examineeShortList: [],
  loading: LoadingState.notLoading,
};

@State<ExamineeStateModel>({
  name: 'examinee',
  defaults
})

@Injectable()
export class ExamineeState {

  applicationService: ApplicationService = inject(ApplicationService);

  @Selector()
  static listExaminees(state: ExamineeStateModel): IExaminee[] {
    return state?.examineeList ?? [];
  }

  @Selector()
  static listShortExaminees(state: ExamineeStateModel): IExamineeShort[] {
    return state?.examineeShortList ?? [];
  }

  @Selector()
  static getLoading(state: ExamineeStateModel): LoadingState {
    return state.loading;
  }


  @Action(ExamineeAction)
  add({ getState, setState }: StateContext<ExamineeStateModel>, { payload }: ExamineeAction) {
    const state = getState();
    setState({ ...state, examineeList: [...state.examineeList, payload] });
  }


  @Action(ClearExamineesAction)
  clearExaminees(ctx: StateContext<ExamineeStateModel>) {
    ctx.patchState({ examineeList: [] });
  }


  @Action(GetExamApplicantsAction)
  getExamApplicants(ctx: StateContext<ExamineeStateModel>, action: GetExamApplicantsAction) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getExamApplicants(action.payload).pipe(
      tap((response: { data: IExaminee[] }) => {
        console.log('getting the latest state', response)
        ctx.patchState({ examineeList: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    )
  }


  @Action(GetExamApplicantsShortAction)
  getExamApplicantsShort(ctx: StateContext<ExamineeStateModel>, action: GetExamApplicantsShortAction) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getExamApplicantsShort(action.payload).pipe(
      tap((response: { data: IExamineeShort[] }) => {
        ctx.patchState({ examineeShortList: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    )
  }

  @Action(GetSingleExamApplicantAction)
  getSingleExamApplicant(ctx: StateContext<ExamineeStateModel>, action: GetSingleExamApplicantAction) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getSingleExamApplicant(action.payload).pipe(
      tap((response: { data: IExamineeShort[] }) => {
        ctx.patchState({ examineeShortList: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    )
  }

  @Action(UpdateExamineeStatusAction)
  updateExamineeStatus(ctx: StateContext<ExamineeStateModel>, action: UpdateExamineeStatusAction) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.updateExamineeStatus(action.payload).pipe(
      tap((response: { message: string }) => {
        // Update the local state by modifying the status of affected examinees
        const state = ctx.getState();
        const updatedExaminees = state.examineeList.map(examinee => {
          if (action.payload.applicationIds.includes(examinee.id)) {
            return { ...examinee, status: action.payload.status };
          }
          return examinee;
        });
        ctx.patchState({
          examineeList: updatedExaminees,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(AssignExamAdminAction)
  assignExamAdmin(ctx: StateContext<ExamineeStateModel>, action: AssignExamAdminAction) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.assignExamAdmin(action.payload).pipe(
      tap(() => {
        ctx.patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(ReassignExamineeAction)
  reassignExaminee(ctx: StateContext<ExamineeStateModel>, action: ReassignExamineeAction) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.reassignExaminee(action.payload).pipe(
      tap((response: any) => {
        // Update the local state by modifying the exam ID of affected examinees
        const state = ctx.getState();
        console.log('response', response);
        const updatedExaminees = state.examineeList.map(examinee => {
          if (action.payload.applicationIds.includes(examinee.id)) {
            return { ...examinee, examId: action.payload.examId };
          }
          return examinee;
        });
        ctx.patchState({
          examineeList: updatedExaminees,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

}
